import apiClient, { checkAdminStatus } from './apiClient'
import router from '@/router/index'
import { CUSTOMER_TYPE } from '@/shared/constants/customerTypes'

export const AUTH_SESSION_KEYS = Object.freeze({
  accessToken: 'accessToken',
  refreshToken: 'refreshToken',
  username: 'username',
  isAdmin: 'isAdmin',
  userId: 'userId',
  userEmail: 'userEmail',
  customerType: 'customerType',
  distributorPendingApproval: 'distributorPendingApproval',
  maintenanceMode: 'maintenance_mode',
  maintenanceBypass: 'maintenance_bypass',
  maintenanceBypassExpiry: 'maintenance_bypass_expiry',
})

const LOCALE_STORAGE_KEY = 'godea_locale'
const resolveStoredLocale = (value) => (value === 'en' ? 'en' : 'es')

const hasOwn = (data, key) => Object.prototype.hasOwnProperty.call(data, key)

const writeStorageValue = (key, value) => {
  if (value === undefined || value === null || value === '') {
    localStorage.removeItem(key)
    return
  }
  localStorage.setItem(key, String(value))
}

export const readAuthSession = () => ({
  accessToken: localStorage.getItem(AUTH_SESSION_KEYS.accessToken),
  refreshToken: localStorage.getItem(AUTH_SESSION_KEYS.refreshToken),
  username: localStorage.getItem(AUTH_SESSION_KEYS.username) || '',
  isAdmin: localStorage.getItem(AUTH_SESSION_KEYS.isAdmin) === 'true',
  userId: localStorage.getItem(AUTH_SESSION_KEYS.userId),
  userEmail: localStorage.getItem(AUTH_SESSION_KEYS.userEmail) || '',
  customerType: localStorage.getItem(AUTH_SESSION_KEYS.customerType) || CUSTOMER_TYPE.REGULAR,
  distributorPendingApproval: localStorage.getItem(AUTH_SESSION_KEYS.distributorPendingApproval) === 'true',
})

export const persistAuthSession = (session = {}) => {
  if (hasOwn(session, 'accessToken')) writeStorageValue(AUTH_SESSION_KEYS.accessToken, session.accessToken)
  if (hasOwn(session, 'refreshToken')) writeStorageValue(AUTH_SESSION_KEYS.refreshToken, session.refreshToken)
  if (hasOwn(session, 'username')) writeStorageValue(AUTH_SESSION_KEYS.username, session.username)
  if (hasOwn(session, 'isAdmin')) localStorage.setItem(AUTH_SESSION_KEYS.isAdmin, session.isAdmin ? 'true' : 'false')
  if (hasOwn(session, 'userId')) writeStorageValue(AUTH_SESSION_KEYS.userId, session.userId)
  if (hasOwn(session, 'userEmail')) writeStorageValue(AUTH_SESSION_KEYS.userEmail, session.userEmail)
  if (hasOwn(session, 'customerType')) writeStorageValue(AUTH_SESSION_KEYS.customerType, session.customerType || CUSTOMER_TYPE.REGULAR)
  if (hasOwn(session, 'distributorPendingApproval')) {
    if (typeof session.distributorPendingApproval === 'boolean') {
      localStorage.setItem(
        AUTH_SESSION_KEYS.distributorPendingApproval,
        session.distributorPendingApproval ? 'true' : 'false',
      )
    } else {
      localStorage.removeItem(AUTH_SESSION_KEYS.distributorPendingApproval)
    }
  }
}

export const clearMaintenanceSession = () => {
  localStorage.removeItem(AUTH_SESSION_KEYS.maintenanceMode)
  localStorage.removeItem(AUTH_SESSION_KEYS.maintenanceBypass)
  localStorage.removeItem(AUTH_SESSION_KEYS.maintenanceBypassExpiry)
}

export const clearAuthSessionStorage = () => {
  localStorage.removeItem(AUTH_SESSION_KEYS.accessToken)
  localStorage.removeItem(AUTH_SESSION_KEYS.refreshToken)
  localStorage.removeItem(AUTH_SESSION_KEYS.isAdmin)
  localStorage.removeItem(AUTH_SESSION_KEYS.username)
  localStorage.removeItem(AUTH_SESSION_KEYS.userId)
  localStorage.removeItem(AUTH_SESSION_KEYS.userEmail)
  localStorage.removeItem(AUTH_SESSION_KEYS.customerType)
  localStorage.removeItem(AUTH_SESSION_KEYS.distributorPendingApproval)
  clearMaintenanceSession()
}

export const setAdminSessionState = (isAdmin) => {
  localStorage.setItem(AUTH_SESSION_KEYS.isAdmin, isAdmin ? 'true' : 'false')
  if (isAdmin) {
    localStorage.setItem(AUTH_SESSION_KEYS.maintenanceMode, 'false')
  }
}

export const login = async (username, password) => {
  try {
    // Obtener el ID anónimo del carrito del localStorage si existe
    const anonymousCartId = localStorage.getItem('cart_anonymous_id');
    
    const response = await apiClient.post('/auth/login/', {
      username,
      password,
      anonymous_cart_id: anonymousCartId // Enviar el ID anónimo si existe
    })
    
    if (response.data.access) {
      // Limpiar primero cualquier estado previo
      clearAuthSessionStorage();

      const isAdmin = response.data.is_staff === true || response.data.is_admin === true;

      persistAuthSession({
        accessToken: response.data.access,
        refreshToken: response.data.refresh,
        isAdmin,
        username,
        customerType: response.data.customer_type || CUSTOMER_TYPE.REGULAR,
        distributorPendingApproval: response.data.distributor_pending_approval,
      });

      if (response.data.cart_linked) {
        localStorage.removeItem('cart_anonymous_id');
        await import('@/services/tempCart')
          .then((mod) => mod.clearGuestCartSnapshot())
          .catch(() => undefined);
      }

      try {
        const store = await import('@/store').then((module) => module.default);
        store.commit('SET_SYNC_ENABLED', false);
        await store.dispatch('mergeLocalAndServerCarts');
        setTimeout(() => {
          store.commit('SET_SYNC_ENABLED', true);
        }, 2000);
      } catch (e) {
        console.error('Error al sincronizar carrito post-login:', e);
      }
      
      // SOLO si es admin, forzar maintenance_mode a false
      // Para usuarios normales, se respeta el valor del servidor
      if (isAdmin) {
        setAdminSessionState(true);
      } else {
        // Para usuarios no-admin, verificar si hay mantenimiento activo
        try {
          const maintenanceService = (await import('@/services/maintenanceService')).default;
          const inMaintenance = await maintenanceService.isInMaintenance(true); // forzar verificación
          
          // Si hay mantenimiento y no es admin, redirigir a mantenimiento después del login
          if (inMaintenance) {
            router.push({ name: 'Maintenance' });
          }
        } catch (e) {
          console.error('Error verificando estado de mantenimiento después del login:', e);
        }
      }
      
      // Reiniciar cache de mantenimiento al hacer login
      try {
        const maintenanceService = (await import('@/services/maintenanceService')).default;
        await maintenanceService.resetMaintenanceCache(false);
      } catch (e) {
        console.error('Error al reiniciar cache de mantenimiento:', e);
      }
      
      return response.data
    }
    
    throw new Error('No se recibió token de acceso')
  } catch (error) {
    if (error.response && error.response.data) {
      throw error;
    }
    throw new Error('Error al iniciar sesión');
  }
}

export const register = async (userData) => {
  const anonymousCartId = localStorage.getItem('cart_anonymous_id')
  const preferredLanguage = resolveStoredLocale(localStorage.getItem(LOCALE_STORAGE_KEY))

  // Payload explícito (evita proxies de Vue, campos extra y mutar el reactive original)
  const payload = {
    email: userData.email,
    password: userData.password,
    password2: userData.password2,
    first_name: userData.first_name,
    last_name: userData.last_name,
    phone: userData.phone,
    business_name: userData.business_name || '',
    instagram: userData.instagram || '',
    preferred_language: preferredLanguage,
    register_as_distributor: Boolean(userData.register_as_distributor),
  }
  if (anonymousCartId) {
    payload.anonymous_cart_id = anonymousCartId
  }

  const response = await apiClient.post('/auth/register/', payload)

  if (response.status >= 200 && response.status < 300) {
    const d = response.data && typeof response.data === 'object' ? response.data : {}
    return {
      message: d.message,
      email: d.email ?? userData.email,
      userId: d.user_id,
      resend: d.resend === true,
    }
  }

  throw new Error('Error en el registro')
}

export const logout = () => {
  clearAuthSessionStorage()
}

export const isAuthenticated = () => {
  return readAuthSession().accessToken !== null
}

export const isAdmin = async (forceCheck = false) => {
  // IMPORTANTE: Esta función es CRUCIAL para el acceso en mantenimiento
  
  // Primero revisar el localStorage para respuesta inmediata
  const session = readAuthSession();
  const localAdminStatus = session.isAdmin;
  const token = session.accessToken;
  
  // Verificación rápida: sin token, no puede ser admin
  if (!token) {
    setAdminSessionState(false);
    return false;
  }
  
  // Si no es necesario forzar la verificación y tenemos un valor local, usarlo
  if (!forceCheck && localAdminStatus) {
    setAdminSessionState(true);
    return localAdminStatus;
  }
  
  // Verificar con el servidor si el usuario realmente es administrador
  try {
    // Usar endpoint específico para verificación de admin
    const isAdminStatus = await checkAdminStatus(true); // Forzar verificación con servidor
    
    // Actualizar el localStorage con el valor más reciente
    setAdminSessionState(isAdminStatus);

    return isAdminStatus;
  } catch (error) {
    console.error('[Auth] Error al verificar estado de administrador:', error);
    
    // Si hay error pero tenemos un token y localmente es admin, confiar en eso
    if (token && localAdminStatus) {
      setAdminSessionState(true);
      return true;
    }
    
    return false; // En caso de error y sin confirmación previa, asumir no admin
  }
}

export const getUsername = () => {
  return readAuthSession().username
}

export const getAccessToken = () => {
  return readAuthSession().accessToken
}

export const getUserInfo = async () => {
  try {
    const token = getAccessToken()
    const response = await apiClient.get('/auth/profile/', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al obtener información del usuario')
  }
}

export const updateUserProfile = async (userData) => {
  try {
    const response = await apiClient.put('/auth/profile/', userData)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Error al actualizar el perfil'
    throw new Error(errorMessage)
  }
}

export const changePassword = async ({old_password, new_password}) => {
  try {
    const response = await apiClient.post('/auth/change-password/', {
      old_password,
      new_password
    })
    // Update token after password change if provided
    if (response.data.access) {
      persistAuthSession({ accessToken: response.data.access })
    }
    return response.data
  } catch (error) {
    throw error.response?.data || error
  }
}

export const verifyEmail = async (data) => {
  // Obtener el ID anónimo del carrito del localStorage si existe
  const anonymousCartId = localStorage.getItem('cart_anonymous_id');
  
  // Preparar los datos para la verificación
  const verificationData = {
    email: data.email,
    code: data.code
  };
  
  // Añadir el ID anónimo si existe
  if (anonymousCartId) {
    verificationData.anonymous_cart_id = anonymousCartId;
  }
  
  const response = await apiClient.post('/auth/verify-email/', verificationData);
  
  // Si el carrito se ha vinculado exitosamente, eliminar el ID anónimo
  if (response.data.cart_linked) {
    localStorage.removeItem('cart_anonymous_id');
    await import('@/services/tempCart')
      .then((mod) => mod.clearGuestCartSnapshot())
      .catch(() => undefined);
    
    // Actualizar el carrito en la UI
    try {
      const store = await import('@/store').then(module => module.default);
      store.commit('SET_SYNC_ENABLED', false);
      await store.dispatch('mergeLocalAndServerCarts');
      setTimeout(() => {
        store.commit('SET_SYNC_ENABLED', true);
      }, 2000);
    } catch (e) {
      console.error('Error al sincronizar carrito después de la verificación:', e);
    }
  }
  
  return response.data;
};

export const verifyEmailLink = async (token) => {
  const anonymousCartId = localStorage.getItem('cart_anonymous_id');
  const payload = { token };

  if (anonymousCartId) {
    payload.anonymous_cart_id = anonymousCartId;
  }

  const response = await apiClient.post('/auth/verify-email-link/', payload);

  if (response.data.cart_linked) {
    localStorage.removeItem('cart_anonymous_id');
    await import('@/services/tempCart')
      .then((mod) => mod.clearGuestCartSnapshot())
      .catch(() => undefined);

    try {
      const store = await import('@/store').then(module => module.default);
      store.commit('SET_SYNC_ENABLED', false);
      await store.dispatch('mergeLocalAndServerCarts');
      setTimeout(() => {
        store.commit('SET_SYNC_ENABLED', true);
      }, 2000);
    } catch (e) {
      console.error('Error al sincronizar carrito después de activar email:', e);
    }
  }

  return response.data;
};

export const resendVerification = async (email) => {
  const response = await apiClient.post('/auth/resend-verification/', {
    email
  });
  return response.data;
};

export const requestVerificationCode = async (type, newEmail = null, password = null) => {
  try {
    const token = getAccessToken()
    const response = await apiClient.post('/auth/verify-change/', {
      type,
      new_email: newEmail,
      password: password
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    return response.data
  } catch (error) {
    console.error('Verification code request error:', error.response?.data || error) // Debug
    
    // Manejar casos específicos de error
    if (error.response?.data) {
      // Contraseña incorrecta
      if (error.response.data.password && Array.isArray(error.response.data.password)) {
        throw { error: 'invalid_password', message: 'La contraseña es incorrecta' };
      }
      
      // Email ya existente
      if (error.response.data.new_email && Array.isArray(error.response.data.new_email)) {
        const emailErrors = error.response.data.new_email;
        if (emailErrors.some(err => err.includes('exist') || err.includes('registrado'))) {
          throw { error: 'email_already_exists', message: 'Este email ya está registrado por otro usuario' };
        }
      }

      // Si hay un mensaje de error específico
      if (typeof error.response.data === 'object' && error.response.data.error) {
        throw error.response.data;
      }
    }
    
    // Error genérico si no pudimos identificar uno específico
    throw error.response?.data || { error: 'unknown_error', message: 'Error al solicitar verificación' };
  }
}

export const updateEmailWithVerification = async (email, verificationCode) => {
  try {
    const response = await apiClient.put('/auth/profile/', {
      email,
      verification_code: verificationCode
    })
    return response.data
  } catch (error) {
    // Mejor manejo de errores para diferentes casos
    if (error.response?.data) {
      // Si es un error relacionado con email ya existente
      if (error.response.data.email && Array.isArray(error.response.data.email)) {
        const emailErrors = error.response.data.email;
        if (emailErrors.some(err => err.includes('exist'))) {
          throw { error: 'email_already_exists', message: 'Este email ya está registrado' };
        }
      }
      
      // Si es un error relacionado con código inválido
      if (error.response.data.verification_code && Array.isArray(error.response.data.verification_code)) {
        const codeErrors = error.response.data.verification_code;
        if (codeErrors.some(err => err.includes('invalid') || err.includes('expired'))) {
          throw { error: 'invalid_code', message: 'El código de verificación es inválido o ha expirado' };
        }
      }

      // Si hay un mensaje de error genérico
      if (typeof error.response.data === 'object' && error.response.data.error) {
        throw error.response.data;
      }
    }
    
    // Si no pudimos identificar un error específico
    throw error.response?.data || { error: 'unknown_error', message: 'Error al actualizar el email' };
  }
}

export const getLoggedInUser = async () => {
  try {
    // Actualizar también el estado de isAdmin desde el servidor
    await checkAdminStatus(true);
    
    const token = getAccessToken();
    if (!token) return null;

    const response = await apiClient.get('/auth/me/', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user data:', error);
    return null;
  }
};

/**
 * Verificación de EMERGENCIA del estado de administrador
 * Bypasses todas las capas de caché y consulta directamente al servidor
 */
export const forceAdminVerification = async () => {
  try {
    
    // Verificar token
    const token = getAccessToken();
    if (!token) {
      setAdminSessionState(false);
      return { isAdmin: false, verified: true };
    }
    
    // Hacer petición directa al servidor, sin usar apiClient para evitar posibles problemas
    const response = await fetch('http://10.122.7.101:8000/api/auth/verify-admin/', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Error del servidor: ${response.status}`);
    }
    
    const data = await response.json();
    const isAdminStatus = data.is_admin === true;
    
    // Actualizar localStorage directamente
    setAdminSessionState(isAdminStatus);

    return {
      isAdmin: isAdminStatus,
      verified: true,
      serverResponse: data
    };
  } catch (error) {
    console.error('[Auth] Error en verificación de emergencia:', error);
    return { 
      isAdmin: false, 
      verified: false,
      error: error.message 
    };
  }
};

export default {
  login,
  register,
  logout,
  isAuthenticated,
  isAdmin,
  getUsername,
  getAccessToken,
  getUserInfo,
  updateUserProfile,
  changePassword,
  verifyEmail,
  verifyEmailLink,
  resendVerification,
  requestVerificationCode,
  updateEmailWithVerification,
  getLoggedInUser,
  forceAdminVerification,
  readAuthSession,
  persistAuthSession,
  clearAuthSessionStorage,
  clearMaintenanceSession,
  setAdminSessionState,
};
